import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DerivativeCreation } from "@/data/models/types";
import { Card, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { VideoPlayer } from "@/components/ui/VideoPlayer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Eye, Play } from "lucide-react";
import { DerivativeSocialActions } from "./DerivativeSocialActions";
import { Separator } from "@/components/ui/separator";
import { incrementDerivativeView } from "@/utils/supabase/statsTracking";
import { getCreatorInitials } from "@/utils/dataUtils/formatters";
import { useTheme } from "@/contexts/ThemeContext";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import { useIsMobile } from "@/hooks/use-mobile";

export interface UnifiedDerivativeItemProps {
  derivative: DerivativeCreation;
  isVisible?: boolean;
  isInViewport?: boolean;
  forceVertical?: boolean;
}

export const UnifiedDerivativeItem = ({
  derivative,
  isVisible = false,
  isInViewport = false,
  forceVertical = false
}: UnifiedDerivativeItemProps) => {
  const { theme } = useTheme();
  const isMobile = useIsMobile();
  const [isHovered, setIsHovered] = useState(false);
  const [viewTracked, setViewTracked] = useState(false);
  const [isMuted, setIsMuted] = useState(false); // Default to sound ON
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Enhanced debugging to track platform
  useEffect(() => {
    console.log(`UnifiedDerivativeItem (${derivative.id}) - Platform: ${isMobile ? 'Mobile' : 'Desktop'}, isVisible: ${isVisible}, isInViewport: ${isInViewport}`);
  }, [derivative.id, isMobile, isVisible, isInViewport]);
  
  // Debug the data to verify we're receiving correct values - IMPROVED DEBUGGING
  useEffect(() => {
    console.log(`UnifiedDerivativeItem (${derivative.id}) - received data:`, {
      title: derivative.title,
      views: derivative.views,
      viewsType: typeof derivative.views,
      likes: derivative.likesCount,
      likesType: typeof derivative.likesCount,
      favorites: derivative.favoritesCount, 
      favoritesType: typeof derivative.favoritesCount,
      comments: derivative.commentsCount,
      commentsType: typeof derivative.commentsCount
    });
  }, [derivative]);
  
  // Format large numbers nicely
  const formatCount = (count: number | undefined): string => {
    // Ensure count is a number and default to 0 if undefined
    const safeCount = typeof count === 'number' ? count : 0;
    
    if (safeCount >= 1000000) {
      return `${(safeCount / 1000000).toFixed(1)}M`;
    } else if (safeCount >= 1000) {
      return `${(safeCount / 1000).toFixed(1)}K`;
    }
    return safeCount.toString();
  };

  // Track view when the user plays the video, not just when it becomes visible
  const handlePlayStateChange = (isPlaying: boolean) => {
    // Update local playing state
    setIsPlaying(isPlaying);
    
    console.log(`UnifiedDerivativeItem (${derivative.id}) - Play state changed to: ${isPlaying}`);
    
    // Only track view when video starts playing and hasn't been tracked yet
    if (isPlaying && !viewTracked && derivative.id) {
      incrementDerivativeView(derivative.id);
      setViewTracked(true);
    }
  };
  
  // ALWAYS use 9:16 ratio when forceVertical is true, otherwise use derivative's aspectRatio or fallback to 16/9
  const aspectRatio = forceVertical ? 9/16 : (derivative.aspectRatio || 16/9);
  
  // Determine if the video has a wide aspect ratio (horizontal video)
  const isWideAspectRatio = derivative.aspectRatio && derivative.aspectRatio > 1;

  // Ensure views are never negative or undefined
  const viewCount = typeof derivative.views === 'number' ? Math.max(0, derivative.views) : 0;
  
  // Extract social counts from derivative for immediate display - ensure proper formatting
  const socialCounts = {
    likes_count: typeof derivative.likesCount === 'number' ? Math.max(0, derivative.likesCount) : 0,
    favorites_count: typeof derivative.favoritesCount === 'number' ? Math.max(0, derivative.favoritesCount) : 0,
    comments_count: typeof derivative.commentsCount === 'number' ? Math.max(0, derivative.commentsCount) : 0
  };
  
  // Log view count for debugging
  console.log(`UnifiedDerivativeItem (${derivative.id}) - final formatted view count:`, formatCount(viewCount));

  // Toggle play/pause
  const togglePlay = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsPlaying(!isPlaying);
  };

  // Add debug log to check isMobile state
  useEffect(() => {
    console.log(`UnifiedDerivativeItem (${derivative.id}) - isMobile:`, isMobile);
  }, [isMobile, derivative.id]);

  return (
    <Card 
      className="group overflow-hidden bg-card hover:shadow-lg transition-all duration-300 h-full flex flex-col"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Fixed height and aspect ratio container */}
      <div className={`relative flex-shrink-0 ${forceVertical ? 'aspect-[9/16]' : ''}`}>
        <AspectRatio 
          ratio={aspectRatio}
          className="flex justify-center items-center bg-black/5"
          centerContent={forceVertical ? isWideAspectRatio : (derivative.aspectRatio && derivative.aspectRatio > 1)}
        >
          <VideoPlayer
            videoSrc={derivative.videoUrl}
            posterSrc={derivative.thumbnail}
            autoplay={false} // Important: Never autoplay
            loop={true}
            muted={false} // Default to sound ON
            hideControls={true}
            onPlayStateChange={handlePlayStateChange}
            isPlaying={isPlaying} // Control playing state via isPlaying prop
            className={`w-full h-full ${isWideAspectRatio ? 'object-contain' : 'object-cover'} rounded-t-lg`}
            showSoundControls={isPlaying} // Only show sound controls when playing
            isMobile={isMobile} // Pass the isMobile prop to VideoPlayer
            forceFirstFrame={isMobile} // FIXED: Only force first frame on mobile devices
          />
          
          {/* Play button overlay */}
          {!isPlaying && (
            <div 
              className="absolute inset-0 flex items-center justify-center cursor-pointer"
              onClick={togglePlay}
            >
              <div className="bg-black/30 backdrop-blur-sm rounded-full p-3 w-12 h-12 flex items-center justify-center transition-transform duration-200 hover:scale-110">
                <Play className="h-5 w-5 text-white" />
              </div>
            </div>
          )}
        </AspectRatio>
      </div>

      <Link to={`/derivative/${derivative.id}`} className="flex-1 flex flex-col">
        <CardHeader className="p-4 pb-2 mt-auto">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-1.5">
              <CardTitle className="text-base font-semibold line-clamp-1">
                {derivative.title}
              </CardTitle>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Avatar className="h-6 w-6">
                  <AvatarImage src={derivative.creatorAvatar} />
                  <AvatarFallback>{getCreatorInitials(derivative.creatorName)}</AvatarFallback>
                </Avatar>
                <span className="line-clamp-1">{derivative.creatorName}</span>
              </div>
            </div>
            {derivative.expressionType && (
              <Badge variant="secondary" className="shrink-0">
                {derivative.expressionType}
              </Badge>
            )}
          </div>
        </CardHeader>
      </Link>
      
      {/* Social metrics footer with views separate from interactive social actions */}
      <CardFooter className="px-2 py-2 sm:px-4 sm:py-2.5 border-t flex items-center justify-between text-xs mt-auto bg-gradient-to-r from-background/70 to-background/40">
        <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
          <Eye className="h-3.5 w-3.5" />
          <span className="tabular-nums">{formatCount(viewCount)}</span>
        </div>
        
        <Separator orientation="vertical" className="h-5 mx-1 sm:mx-3 hidden sm:block" />
        
        <ErrorBoundary>
          <DerivativeSocialActions 
            derivativeId={derivative.id}
            size="xs"
            initialCounts={socialCounts}
            className={`ml-auto ${theme === 'light' ? 'text-gray-700' : 'text-white'}`}
          />
        </ErrorBoundary>
      </CardFooter>
    </Card>
  );
};

export default UnifiedDerivativeItem;
